import { getAuth } from 'firebase/auth';
import type { ApiErrorResponse, ApiSuccessResponse, Stay } from '../types/index.js';

// Select the correct API base URL based on the emulator toggle.
// Set VITE_FIREBASE_USE_EMULATOR=true/false in .env.local to switch modes;
// the correct URL is picked automatically from VITE_API_BASE_URL_EMULATOR or
// VITE_API_BASE_URL_PROD without needing to edit the URL itself.
const getBaseUrl = (): string => {
  const useEmulator = import.meta.env.VITE_FIREBASE_USE_EMULATOR === 'true';
  const key = useEmulator ? 'VITE_API_BASE_URL_EMULATOR' : 'VITE_API_BASE_URL_PROD';
  const url = import.meta.env[key] as string | undefined;
  if (!url) {
    throw new Error(`Missing required environment variable: ${key}. Check your .env.local file.`);
  }
  return url;
};

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getIdToken(forceRefresh = false): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError(401, 'UNAUTHORIZED', 'No authenticated user.');
  }
  return user.getIdToken(forceRefresh);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<ApiSuccessResponse<T>> {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${path}`;

  const makeRequest = async (forceRefresh = false): Promise<Response> => {
    const idToken = await getIdToken(forceRefresh);
    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let response = await makeRequest();

  // Retry once with a fresh token on 401
  if (response.status === 401) {
    response = await makeRequest(true);
  }

  const json = (await response.json()) as ApiSuccessResponse<T> | ApiErrorResponse;

  if (!response.ok) {
    const errorResponse = json as ApiErrorResponse;
    throw new ApiError(response.status, errorResponse.error.code, errorResponse.error.message);
  }

  return json as ApiSuccessResponse<T>;
}

// Impersonation is handled server-side via POST /admin/impersonate/:userId.
// There is no client-side impersonation header — the backend does not read one.
export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),

  stays: {
    list: (entityId: string, buildingId: string) =>
      request<Stay[]>('GET', `/api/entities/${entityId}/buildings/${buildingId}/stays`),
    create: (
      entityId: string,
      buildingId: string,
      body: { guestName: string; checkIn: string; checkOut: string; memo: string },
    ) => request<Stay>('POST', `/api/entities/${entityId}/buildings/${buildingId}/stays`, body),
    update: (
      entityId: string,
      buildingId: string,
      stayId: string,
      body: Partial<{ guestName: string; checkIn: string; checkOut: string; memo: string }>,
    ) =>
      request<{ success: boolean }>(
        'PATCH',
        `/api/entities/${entityId}/buildings/${buildingId}/stays/${stayId}`,
        body,
      ),
    delete: (entityId: string, buildingId: string, stayId: string) =>
      request<{ success: boolean }>(
        'DELETE',
        `/api/entities/${entityId}/buildings/${buildingId}/stays/${stayId}`,
      ),
  },

  ai: {
    buildingSummary: (
      entityId: string,
      body: {
        buildings: Array<{
          id: string;
          name: string;
          city: string;
          state: string;
          onlineDevices: number;
          totalDevices: number;
          temperature: string;
          humidity: string;
          lux: string;
          occupancy: string;
          outlets: string;
          activeAlerts: number;
          alertSeverity: string | null;
        }>;
      },
    ) =>
      request<{ summary: string }>('POST', `/api/entities/${entityId}/ai/building-summary`, body),
  },
};

export { ApiError };
